import React, {
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  FaFlag,
  FaRobot,
  FaRedoAlt,
  FaShieldAlt,
  FaUserFriends,
} from "react-icons/fa";
import ChessBoard from "./ChessBoard";
import ChessSidebar from "./ChessSidebar";
import {
  allLegalMoves,
  createInitialBoard,
  createInitialGameState,
  getGameStatus,
  legalMovesForPiece,
  makeMove,
  opposite,
  positionKey,
} from "./chessEngine";
import {
  AI_LEVELS,
  chooseAiMove,
} from "./chessAI";
import {
  loadChessAppearance,
  saveChessAppearance,
} from "./chessThemes";
import "./Chess.css";
import "./ChessThemes.css";
import GameRoomBackButton from "../GameRoomBackButton";

function initialPositionCounts() {
  const board = createInitialBoard();
  const state = createInitialGameState();

  return {
    [positionKey(
      board,
      state
    )]: 1,
  };
}

export default function GameRoomChess() {

  const initialAppearance =
    useMemo(
      () => loadChessAppearance(),
      []
    );

  const [board, setBoard] =
    useState(() =>
      createInitialBoard()
    );

  const [state, setState] =
    useState(() =>
      createInitialGameState()
    );

  const [selected, setSelected] =
    useState(null);

  const [legalMoves, setLegalMoves] =
    useState([]);

  const [history, setHistory] =
    useState([]);

  const [
    positionCounts,
    setPositionCounts,
  ] = useState(
    initialPositionCounts
  );

  const [
    pendingPromotion,
    setPendingPromotion,
  ] = useState(null);

  const [lastMove, setLastMove] =
    useState(null);

  const [gameMode, setGameMode] =
    useState("ai");

  const [
    aiDifficulty,
    setAiDifficulty,
  ] = useState("medium");

  const [aiThinking, setAiThinking] =
    useState(false);

  const [aiInfo, setAiInfo] =
    useState(null);

  const [humanColor, setHumanColor] =
    useState("w");

  const [boardTheme, setBoardTheme] =
    useState(
      initialAppearance.boardTheme
    );

  const [pieceSet, setPieceSet] =
    useState(
      initialAppearance.pieceSet
    );

  const [
    showCoordinates,
    setShowCoordinates,
  ] = useState(
    initialAppearance.showCoordinates
  );

  const [orientation, setOrientation] =
    useState("w");

  const [gameResult, setGameResult] =
    useState({
      over: false,
      reason: "",
      winner: null,
      check: false,
      claimable: null,
    });

  const aiColor =
    humanColor === "w"
      ? "b"
      : "w";

  const currentTurnLabel =
    state.turn === "w"
      ? "White"
      : "Black";

  const statusText =
    useMemo(() => {
      if (!gameResult.over) {
        if (aiThinking) {
          return "Hobbli AI is thinking...";
        }

        return gameResult.check
          ? `${currentTurnLabel} is in check`
          : `${currentTurnLabel} to move`;
      }

      if (
        gameResult.reason ===
        "checkmate"
      ) {
        return `${
          gameResult.winner === "w"
            ? "White"
            : "Black"
        } wins by checkmate`;
      }

      if (
        gameResult.reason ===
        "conceded"
      ) {
        return `${
          gameResult.winner === "w"
            ? "White"
            : "Black"
        } wins by resignation`;
      }

      const labels = {
        stalemate:
          "Draw by stalemate",
        insufficient:
          "Draw by insufficient material",
        "seventy-five-move":
          "Automatic draw by the 75-move rule",
        fivefold:
          "Automatic draw by fivefold repetition",
        "claimed-fifty":
          "Draw claimed under the 50-move rule",
        "claimed-threefold":
          "Draw claimed by threefold repetition",
      };

      return (
        labels[gameResult.reason] ||
        "Game over"
      );
    }, [
      gameResult,
      currentTurnLabel,
      aiThinking,
    ]);

  const resetGame = ({
    nextHumanColor = humanColor,
  } = {}) => {
    const nextBoard =
      createInitialBoard();

    const nextState =
      createInitialGameState();

    setBoard(nextBoard);
    setState(nextState);
    setSelected(null);
    setLegalMoves([]);
    setHistory([]);
    setPendingPromotion(null);
    setLastMove(null);
    setAiThinking(false);
    setAiInfo(null);
    setPositionCounts({
      [positionKey(
        nextBoard,
        nextState
      )]: 1,
    });

    setGameResult({
      over: false,
      reason: "",
      winner: null,
      check: false,
      claimable: null,
    });

    setOrientation(
      gameMode === "ai"
        ? nextHumanColor
        : "w"
    );
  };

  const completeMove = (
    from,
    move,
    promotionPiece = "Q"
  ) => {
    const result = makeMove(
      board,
      state,
      from,
      move,
      promotionPiece
    );

    const key = positionKey(
      result.board,
      result.state
    );

    const nextCount =
      (positionCounts[key] || 0) + 1;

    const nextCounts = {
      ...positionCounts,
      [key]: nextCount,
    };

    const nextGameResult =
      getGameStatus(
        result.board,
        result.state,
        nextCount
      );

    setBoard(result.board);
    setState(result.state);
    setPositionCounts(nextCounts);

    setHistory((prev) => [
      ...prev,
      result.notation,
    ]);

    setSelected(null);
    setLegalMoves([]);
    setPendingPromotion(null);

    setLastMove({
      from,
      to: {
        r: move.r,
        c: move.c,
      },
    });

    setGameResult(
      nextGameResult
    );
  };

  const handleSquareClick = (
    r,
    c
  ) => {
    if (
      gameResult.over ||
      pendingPromotion ||
      aiThinking
    ) {
      return;
    }

    if (
      gameMode === "ai" &&
      state.turn !== humanColor
    ) {
      return;
    }

    const piece = board[r][c];

    if (selected) {
      const move =
        legalMoves.find(
          (candidate) =>
            candidate.r === r &&
            candidate.c === c
        );

      if (move) {
        if (move.promotion) {
          setPendingPromotion({
            from: selected,
            move,
          });
          return;
        }

        completeMove(
          selected,
          move
        );

        return;
      }
    }

    if (
      piece &&
      piece[0] === state.turn
    ) {
      setSelected({
        r,
        c,
      });

      setLegalMoves(
        legalMovesForPiece(
          board,
          r,
          c,
          state
        )
      );

      return;
    }

    setSelected(null);
    setLegalMoves([]);
  };

  const handleModeChange = (
    nextMode
  ) => {
    if (nextMode === gameMode) {
      return;
    }

    setGameMode(nextMode);

    const nextColor =
      nextMode === "ai"
        ? humanColor
        : "w";

    const nextBoard =
      createInitialBoard();

    const nextState =
      createInitialGameState();

    setBoard(nextBoard);
    setState(nextState);
    setSelected(null);
    setLegalMoves([]);
    setHistory([]);
    setPendingPromotion(null);
    setLastMove(null);
    setAiThinking(false);
    setAiInfo(null);
    setPositionCounts({
      [positionKey(
        nextBoard,
        nextState
      )]: 1,
    });

    setGameResult({
      over: false,
      reason: "",
      winner: null,
      check: false,
      claimable: null,
    });

    setOrientation(nextColor);
  };

  const handleHumanColorChange = (
    color
  ) => {
    setHumanColor(color);
    resetGame({
      nextHumanColor: color,
    });
  };

  const resign = () => {
    if (gameResult.over) {
      return;
    }

    setGameResult({
      over: true,
      reason: "conceded",
      winner:
        gameMode === "ai"
          ? aiColor
          : opposite(state.turn),
      check: false,
      claimable: null,
    });
  };

  const claimDraw = (type) => {
    if (
      type === "threefold" &&
      gameResult.claimable
        ?.threefold
    ) {
      setGameResult({
        over: true,
        reason:
          "claimed-threefold",
        winner: null,
        check:
          gameResult.check,
        claimable: null,
      });
    }

    if (
      type === "fifty" &&
      gameResult.claimable
        ?.fiftyMove
    ) {
      setGameResult({
        over: true,
        reason:
          "claimed-fifty",
        winner: null,
        check:
          gameResult.check,
        claimable: null,
      });
    }
  };

  useEffect(() => {
    saveChessAppearance({
      boardTheme,
      pieceSet,
      showCoordinates,
    });
  }, [
    boardTheme,
    pieceSet,
    showCoordinates,
  ]);

  useEffect(() => {
    if (
      gameMode !== "ai" ||
      state.turn !== aiColor ||
      gameResult.over ||
      pendingPromotion
    ) {
      return;
    }

    let cancelled = false;

    setAiThinking(true);

    const timer =
      window.setTimeout(() => {
        const result =
          chooseAiMove(
            board,
            state,
            aiDifficulty
          );

        if (
          !cancelled &&
          result.move
        ) {
          setAiInfo({
            depth: result.depth,
            nodes: result.nodes,
            elapsedMs:
              result.elapsedMs,
          });

          completeMove(
            result.move.from,
            result.move.move,
            "Q"
          );
        }

        if (!cancelled) {
          setAiThinking(false);
        }
      }, 320);

    return () => {
      cancelled = true;
      window.clearTimeout(
        timer
      );
    };
  }, [
    gameMode,
    state.turn,
    aiColor,
    gameResult.over,
    pendingPromotion,
    board,
    aiDifficulty,
  ]);

  const currentLevel =
    AI_LEVELS[aiDifficulty];

  const boardDisabled =
    gameResult.over ||
    aiThinking ||
    Boolean(
      pendingPromotion
    ) ||
    (
      gameMode === "ai" &&
      state.turn !== humanColor
    );

  return (
    <div className="hobbli-chess">
      <header className="chess-page-header">
        <GameRoomBackButton />

        <div>
          <span>
            Hobbli Game Room
          </span>
          <h1>Chess</h1>
        </div>

        <div className="chess-rule-chip">
          <FaShieldAlt />
          Full legal move validation
        </div>
      </header>

      <section className="chess-mode-bar">
        <div className="chess-mode-toggle">
          <button
            type="button"
            className={
              gameMode === "ai"
                ? "is-active"
                : ""
            }
            onClick={() =>
              handleModeChange("ai")
            }
          >
            <FaRobot />
            Play AI
          </button>

          <button
            type="button"
            className={
              gameMode === "local"
                ? "is-active"
                : ""
            }
            onClick={() =>
              handleModeChange(
                "local"
              )
            }
          >
            <FaUserFriends />
            2 Players
          </button>
        </div>

        {gameMode === "ai" && (
          <div className="chess-mode-summary">
            <span>
              {currentLevel.label} AI
            </span>
            <strong>
              You are{" "}
              {humanColor === "w"
                ? "White"
                : "Black"}
            </strong>
          </div>
        )}
      </section>

      <div className="chess-layout">
        <main className="chess-main">
          <section
            className={[
              "chess-status-card",
              gameResult.check
                ? "is-check"
                : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            <div>
              <span>Game status</span>
              <strong>
                {statusText}
              </strong>
            </div>

            <div className="chess-status-actions">
              {!gameResult.over ? (
                <button
                  onClick={resign}
                  className="chess-secondary chess-danger"
                >
                  <FaFlag />
                  Resign
                </button>
              ) : (
                <button
                  onClick={() =>
                    resetGame()
                  }
                  className="chess-primary"
                >
                  <FaRedoAlt />
                  Play Again
                </button>
              )}
            </div>
          </section>

          {(gameResult.claimable
            ?.threefold ||
            gameResult.claimable
              ?.fiftyMove) &&
            !gameResult.over && (
              <section className="chess-draw-claim">
                <div>
                  <span>
                    Draw available
                  </span>
                  <strong>
                    You may claim a draw
                    under the official
                    rules.
                  </strong>
                </div>

                <div>
                  {gameResult.claimable
                    .threefold && (
                    <button
                      type="button"
                      onClick={() =>
                        claimDraw(
                          "threefold"
                        )
                      }
                    >
                      Claim Threefold
                    </button>
                  )}

                  {gameResult.claimable
                    .fiftyMove && (
                    <button
                      type="button"
                      onClick={() =>
                        claimDraw(
                          "fifty"
                        )
                      }
                    >
                      Claim 50-Move Draw
                    </button>
                  )}
                </div>
              </section>
            )}

          <ChessBoard
            board={board}
            selected={selected}
            legalMoves={legalMoves}
            lastMove={lastMove}
            inCheck={
              gameResult.check
            }
            turn={state.turn}
            boardTheme={boardTheme}
            pieceSet={pieceSet}
            orientation={orientation}
            showCoordinates={
              showCoordinates
            }
            disabled={
              boardDisabled
            }
            onSquareClick={
              handleSquareClick
            }
          />

          <div className="chess-board-footer">
            <span>
              {gameMode === "ai"
                ? aiThinking
                  ? "Hobbli AI is calculating..."
                  : state.turn === humanColor
                  ? "Your move"
                  : "AI move"
                : `${currentTurnLabel} to move`}
            </span>

            <span>
              Board:{" "}
              {boardTheme.replace(
                "-",
                " "
              )}
              {" · "}
              Pieces: {pieceSet}
            </span>
          </div>
        </main>

        <ChessSidebar
          gameMode={gameMode}
          humanColor={humanColor}
          aiDifficulty={
            aiDifficulty
          }
          aiInfo={aiInfo}
          currentTurnLabel={
            currentTurnLabel
          }
          state={state}
          gameResult={gameResult}
          history={history}
          boardTheme={boardTheme}
          pieceSet={pieceSet}
          orientation={orientation}
          showCoordinates={
            showCoordinates
          }
          onAiDifficultyChange={
            setAiDifficulty
          }
          onHumanColorChange={
            handleHumanColorChange
          }
          onBoardThemeChange={
            setBoardTheme
          }
          onPieceSetChange={
            setPieceSet
          }
          onFlipBoard={() =>
            setOrientation(
              (prev) =>
                prev === "w"
                  ? "b"
                  : "w"
            )
          }
          onToggleCoordinates={() =>
            setShowCoordinates(
              (prev) => !prev
            )
          }
        />
      </div>

      {pendingPromotion && (
        <div
          className="chess-promotion-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Choose promotion piece"
        >
          <div className="chess-promotion-dialog">
            <span className="chess-side-eyebrow">
              Pawn promotion
            </span>

            <h3>
              Choose a piece
            </h3>

            <div className="chess-promotion-options">
              {[
                ["Q", "Queen"],
                ["R", "Rook"],
                ["B", "Bishop"],
                ["N", "Knight"],
              ].map(
                ([type, label]) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() =>
                      completeMove(
                        pendingPromotion.from,
                        pendingPromotion.move,
                        type
                      )
                    }
                  >
                    <span>
                      {state.turn === "w"
                        ? {
                            Q: "♕",
                            R: "♖",
                            B: "♗",
                            N: "♘",
                          }[type]
                        : {
                            Q: "♛",
                            R: "♜",
                            B: "♝",
                            N: "♞",
                          }[type]}
                    </span>

                    <strong>
                      {label}
                    </strong>
                  </button>
                )
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
